import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { Button, Card, Select, Skeleton, StatusPill, useToast } from '../../design-system';
import '../styles/ClientRequests.css';

const fmtWhen = (d) =>
  d ? new Date(d).toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  }) : '';

const STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'closed', label: 'Closed' },
];

// The firm's view of one client conversation.
//
// The critical control here is the VISIBILITY toggle on a reply. An internal
// note and a message to the client look almost identical while you're typing
// them, and sending the wrong one is unrecoverable — so the composer states
// which it is at all times and colours itself accordingly.
function OrgAdminClientRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [request, setRequest] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [visibility, setVisibility] = useState('shared_with_client');
  const [sending, setSending] = useState(false);
  const threadEnd = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get(`/api/client-requests/${id}`);
      setRequest(res.data);
      // Files the client attached, plus anything staff linked to this request.
      const docs = await apiClient.get('/api/documents', {
        params: { linkedToType: 'ClientRequest', linkedToId: id },
      });
      setDocuments(docs.data || []);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load this request');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Scroll on the message COUNT, not the whole request — otherwise changing the
  // status would yank the thread while someone is mid-read.
  const messageCount = request?.comments?.length ?? 0;
  useEffect(() => {
    if (messageCount) threadEnd.current?.scrollIntoView({ block: 'nearest' });
  }, [messageCount]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await apiClient.post(`/api/client-requests/${id}/comments`, {
        text: text.trim(),
        visibility,
      });
      setRequest((prev) => ({ ...prev, comments: res.data }));
      setText('');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to post');
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (next) => {
    try {
      const res = await apiClient.put(`/api/client-requests/${id}`, { status: next });
      setRequest(res.data);
      toast.success(`Marked ${next.replace('_', ' ')}`);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to update');
    }
  };

  const spawnTask = async () => {
    try {
      const res = await apiClient.post(`/api/client-requests/${id}/tasks`, {});
      toast.success('Task created');
      navigate(`/org-admin/tasks/${res.data._id || res.data.id}`);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to create a task');
    }
  };

  // Publishing a client-invisible document to the portal, or withdrawing one.
  const toggleShare = async (doc) => {
    try {
      await apiClient.put(`/api/documents/${doc.id}`, { sharedWithClient: !doc.sharedWithClient });
      setDocuments((prev) =>
        prev.map((d) => (d.id === doc.id ? { ...d, sharedWithClient: !d.sharedWithClient } : d))
      );
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not change sharing');
    }
  };

  if (loading) {
    return <OrgAdminLayout title="Client Request"><Card><Skeleton.Text lines={6} /></Card></OrgAdminLayout>;
  }
  if (!request) {
    return (
      <OrgAdminLayout title="Client Request">
        <Card padding="lg">
          <p>Request not found.</p>
          <Link to="/org-admin/client-requests" className="cr-link">Back to requests</Link>
        </Card>
      </OrgAdminLayout>
    );
  }

  const isInternal = visibility === 'internal';

  return (
    <OrgAdminLayout
      title={request.title}
      subtitle={request.clientId?.displayName || ''}
      headerRight={
        <div className="cr-head-actions">
          <Select value={request.status} onChange={(e) => setStatus(e.target.value)} className="cr-status-select">
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
          <Button variant="secondary" size="sm" onClick={spawnTask}>Create task</Button>
        </div>
      }
    >
      <div className="cr-detail">
        <Link to="/org-admin/client-requests" className="cr-link">← All requests</Link>

        <Card padding="md">
          <div className="cr-detail-head">
            <StatusPill status={request.priority} />
            <StatusPill status={request.status} />
            {request.createdByClientUserId && <span className="cr-badge-client">Raised by the client</span>}
          </div>
          {request.description && <p className="cr-desc">{request.description}</p>}
          <p className="cr-muted">
            {request.dueDate ? `Due ${new Date(request.dueDate).toLocaleDateString()}` : 'No due date'}
            {request.assignedUsers?.length > 0 &&
              ` · ${request.assignedUsers.map((u) => u.name).join(', ')}`}
          </p>
        </Card>

        {documents.length > 0 && (
          <Card padding="md">
            <div className="cr-card-head">
              <span className="cr-card-title">Files</span>
              <span className="cr-muted">Toggle to show or hide a file in the client&apos;s portal</span>
            </div>
            <ul className="cr-files">
              {documents.map((d) => (
                <li key={d.id} className="cr-file-row">
                  <span className="cr-file-name">
                    📄 {d.title}
                    {d.fromClient && <span className="cr-badge-client">from client</span>}
                  </span>
                  <label className="cr-share-toggle">
                    <input
                      type="checkbox"
                      checked={!!d.sharedWithClient}
                      onChange={() => toggleShare(d)}
                    />
                    Visible to client
                  </label>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <Card padding="md">
          <div className="cr-card-head"><span className="cr-card-title">Conversation</span></div>

          {(request.comments || []).length === 0 ? (
            <p className="cr-muted">Nothing yet.</p>
          ) : (
            <div className="cr-thread">
              {request.comments.map((c) => (
                <div
                  key={c._id}
                  className={`cr-msg ${c.authorType === 'client' ? 'is-client' : ''} ${
                    c.visibility === 'internal' ? 'is-internal' : ''
                  }`}
                >
                  <div className="cr-msg-head">
                    <strong>{c.authorName || (c.authorType === 'client' ? 'Client' : 'Staff')}</strong>
                    {/* An internal note must be unmistakable in the thread —
                        it's the difference between a private remark and
                        something the client has already read. */}
                    {c.visibility === 'internal' && <span className="cr-internal-tag">Internal only</span>}
                    <span className="cr-muted">{fmtWhen(c.createdAt)}</span>
                  </div>
                  <p className="cr-msg-text">{c.text}</p>
                </div>
              ))}
              <div ref={threadEnd} />
            </div>
          )}

          <form className={`cr-composer${isInternal ? ' is-internal' : ''}`} onSubmit={send}>
            <div className="cr-composer-mode">
              <label>
                <input
                  type="radio"
                  name="cr-visibility"
                  checked={!isInternal}
                  onChange={() => setVisibility('shared_with_client')}
                />
                Reply to client
              </label>
              <label>
                <input
                  type="radio"
                  name="cr-visibility"
                  checked={isInternal}
                  onChange={() => setVisibility('internal')}
                />
                Internal note
              </label>
            </div>
            <textarea
              className="ds-input cr-composer-input"
              rows="3"
              placeholder={isInternal ? 'Note for the team — the client will NOT see this' : 'Write to the client…'}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <div className="cr-composer-actions">
              <span className="cr-muted">
                {isInternal ? '🔒 Only your team will see this' : '👁 The client will see this'}
              </span>
              <Button type="submit" variant="primary" size="sm" loading={sending} disabled={!text.trim()}>
                {isInternal ? 'Save note' : 'Send to client'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </OrgAdminLayout>
  );
}

export default OrgAdminClientRequestDetail;
